import React from 'react';

interface PreviewTreeRowProps {
    icon: React.ReactNode;
    label: string;
    tag: string;
    highlighted?: boolean;
    indented?: boolean;
    onToggle?: () => void;
    expanded?: boolean;
}

export const PreviewTreeRow: React.FC<PreviewTreeRowProps> = ({
    icon,
    label,
    tag,
    highlighted = false,
    indented = false,
    onToggle,
    expanded = true,
}) => {
    const collapsible = typeof onToggle === 'function';
    const rowClass = `flex w-full items-center gap-2 ${indented ? 'pl-5' : ''}`;
    const content = (
        <>
            <span
                className={`flex size-[13px] shrink-0 items-center justify-center ${
                    collapsible ? 'transition-transform duration-150' : ''
                } ${collapsible && !expanded ? '-rotate-90' : ''} ${
                    highlighted
                        ? 'text-[var(--malachite-text-highlight)]'
                        : 'text-[var(--malachite-text-primary)]'
                }`}
            >
                {icon}
            </span>
            <span
                className={`block min-w-0 flex-1 truncate text-left font-medium text-sm tracking-[0.14px] ${
                    highlighted
                        ? 'text-[var(--malachite-text-highlight)]'
                        : 'text-[var(--malachite-text-primary)]'
                }`}
            >
                {label}
            </span>
            <span
                className={`whitespace-nowrap text-right font-medium text-xs tracking-[0.12px] ${
                    highlighted
                        ? 'text-[var(--malachite-text-highlight)] opacity-85'
                        : 'text-[var(--malachite-text-muted)]'
                }`}
            >
                {tag}
            </span>
        </>
    );

    if (collapsible) {
        return (
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className={`${rowClass} cursor-pointer border-0 bg-transparent p-0 transition-opacity hover:opacity-80`}
            >
                {content}
            </button>
        );
    }

    return <div className={rowClass}>{content}</div>;
};

interface PreviewTreeProps {
    children: React.ReactNode;
}

/** A translucent rounded card that groups {@link PreviewTreeRow}s into a tree preview. */
const PreviewTree: React.FC<PreviewTreeProps> = ({ children }) => (
    <div className="flex w-full flex-col gap-4 rounded-2xl bg-white/10 p-5">{children}</div>
);

export default PreviewTree;
